import type { BotIdTags, FormVariant } from "@homeinstead/analytics";
import type { CareOptionType } from "~www/components/LeadForm/CareSeeker";

import {
  identifyLead,
  ServiceLeadFormSubmitEvent,
  track,
  trackLeadCreated,
} from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";

type UseLeadFormTrackingHandlersParams = {
  formVariant: FormVariant;
  formVersionId?: string;
  careFor: string;
  careOptions: CareOptionType[];
  organizationNumber: number;
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  isDebug?: boolean;
};

export function useLeadFormTrackingHandlers({
  formVariant,
  formVersionId,
  careFor,
  careOptions,
  organizationNumber,
  contactInfo,
  isDebug,
}: UseLeadFormTrackingHandlersParams) {
  return {
    trackError: (
      type: "duplicate" | "server-error" | "validation-error",
      tags: BotIdTags,
      validationErrors?: Record<string, string>,
      spamBlockReason?: string,
    ) => {
      if (isDebug) {
        console.log("Error submission, tracking in Segment: ", type);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          formVariant,
          formVersionId,
          franchiseNumber: organizationNumber.toString(),
          recipient: careFor,
          result: type,
          selectedOptions: careOptions,
          spamBlockReason,
          tags,
          validationErrors,
        }),
      );
    },
    trackSuccess: async (leadId: string, tags: BotIdTags) => {
      const { firstName, lastName, email, phone } = contactInfo;

      if (isDebug) {
        console.log("Successful submission, tracking in Segment");
        console.table([
          {
            Email: email,
            "First Name": firstName,
            "Franchise Number": organizationNumber,
            "Last Name": lastName,
            "Lead ID": leadId,
            Phone: phone,
          },
        ]);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          formVariant,
          formVersionId,
          franchiseNumber: organizationNumber.toString(),
          recipient: careFor,
          result: "success",
          selectedOptions: careOptions,
          tags,
        }),
      );

      await identifyLead({
        email,
        firstName,
        lastName,
        phone,
      });

      // Track lead creation - its own event for consistency with the other platform
      // (otherwise we could add lead and organization to the "service-lead-form-submit")
      trackLeadCreated({
        formVariant,
        formVersionId,
        leadId,
        organizationNumber,
        recipient: careFor,
        selectedOptions: careOptions,
        tags,
      });
    },
  };
}
